import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs';

import {
  DynamicDropDownOption,
  DynamicField,
  DynamicFormDefinition,
  DynamicFormLayout,
  DynamicImportResult,
  DynamicTable,
} from '@models/dynamic-form.interface';
import { CalculationEngineService } from '@services/calculation-engine.service';
import { DynamicFormMockService } from '@services/dynamic-form-mock.service';
import { DynamicFormsService } from '@services/dynamic-forms.service';
import { RuleEffect, RuleEngineService } from '@services/rule-engine.service';

/** Per-field runtime state driven by rule effects */
interface FieldState {
  hidden: boolean;
  readonly: boolean;
  required: boolean;
}

@Component({
  selector: 'cvx-dynamic-form-renderer',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './dynamic-form-renderer.component.html',
  styleUrl: './dynamic-form-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicFormRendererComponent implements OnChanges {
  public readonly definition = input<DynamicFormDefinition | null>(null);
  public readonly readOnly = input<boolean>(false);
  public readonly heading = input<string>('Dynamic Form');

  protected readonly form = signal(new FormGroup({}));
  protected readonly selectedFileName = signal('');
  protected readonly importing = signal(false);
  protected readonly importResult = signal<DynamicImportResult | null>(null);
  protected readonly submitMessage = signal('');
  protected readonly submitting = signal(false);

  /** Output/computed fields and their values from the most recent successful submission */
  protected readonly submissionResults = signal<{ label: string; value: unknown }[]>([]);

  /** "tableId:row:col" → evaluated calculation result string */
  protected readonly calcResults = signal<Record<string, string>>({});

  /** fieldId → runtime state (hidden / readonly / required) from rule effects */
  protected readonly fieldStates = signal<Record<string, FieldState>>({});

  /** fieldId → dropdown options loaded dynamically via a "loadOptions" rule action */
  protected readonly fieldDynamicOptions = signal<Record<string, DynamicDropDownOption[]>>({});

  protected readonly layout = computed<DynamicFormLayout>(() => {
    const current = this.definition();
    return current?.layout ?? { type: 'vertical' };
  });

  /**
   * The builder page currently only authors section-based layouts (it has no table-editing UI),
   * so a 'grid' layout with no tables would render nothing. Fall back to the section-based
   * (vertical) rendering whenever there are no tables to display.
   */
  protected readonly renderMode = computed<'grid' | 'vertical'>(() => {
    const current = this.layout();
    return current.type === 'grid' && (current.tables?.length ?? 0) > 0 ? 'grid' : 'vertical';
  });

  /**
   * Fields treated as "outputs" — any field that is the target of a "calculate" rule action
   * defined on another (or the same) field. Everything else is treated as user input.
   */
  protected readonly outputFieldIds = computed<Set<string>>(() => {
    const current = this.definition();
    const ids = new Set<string>();
    for (const field of current?.fields ?? []) {
      for (const rule of field.rules ?? []) {
        if (rule.actions.some((action) => action.type === 'calculate')) {
          ids.add(rule.targetFieldId);
        }
      }
    }
    return ids;
  });

  private readonly calcEngine = inject(CalculationEngineService);
  private readonly ruleEngine = inject(RuleEngineService);
  private readonly mockService = inject(DynamicFormMockService);
  private readonly formsService = inject(DynamicFormsService);
  private readonly destroyRef = inject(DestroyRef);

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['definition']) {
      this.rebuildForm();
    }
  }

  // ─── Template helpers ────────────────────────────────────────────────────────

  protected onFileSelected(event: Event): void {
    const el = event.target as HTMLInputElement;
    const file = el.files?.[0];
    const definition = this.definition();
    if (!file || !definition) return;

    this.selectedFileName.set(file.name);
    this.importing.set(true);

    this.mockService.importFile(file.name, definition.selectedMappingId ?? '', definition).subscribe({
      next: (result) => {
        this.importResult.set(result);
        this.applyImportedData(result.importedData ?? {});
        this.importing.set(false);
      },
      error: () => {
        this.importResult.set({
          success: false,
          message: 'Unable to import file.',
          rowsProcessed: 0,
          rowsImported: 0,
          rowsFailed: 1,
          importedData: {},
          errors: [],
        });
        this.importing.set(false);
      },
    });
  }

  protected submit(): void {
    if (this.readOnly()) return;

    const definition = this.definition();
    if (!definition) return;

    const form = this.form();

    // Force a synchronous rule/calculation pass before reading values. Rules normally re-run
    // via a debounced (80ms) valueChanges subscription, so submitting immediately after an
    // edit could otherwise send stale values that don't reflect the latest calculate/setValue
    // rule effects.
    this.runRules(definition, form);
    this.runCalculations(definition, form);

    if (form.invalid) {
      form.markAllAsTouched();
      this.submitMessage.set('Please correct validation errors before submitting.');
      return;
    }

    this.submitting.set(true);
    this.submitMessage.set('');
    this.submissionResults.set([]);

    const rawValues = form.getRawValue() as Record<string, unknown>;
    const namedValues = this.mapValuesByFieldName(definition, rawValues);

    this.formsService
      .createSubmission(definition.id, {
        testTypeId: definition.testTypeId ? Number(definition.testTypeId) : undefined,
        values: namedValues,
        status: 'Submitted',
      })
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.submitMessage.set('Form submitted successfully.');
          this.submissionResults.set(this.buildResults(definition, rawValues));
        },
        error: () => {
          this.submitting.set(false);
          this.submitMessage.set('Unable to submit the form. Please try again.');
        },
      });
  }

  /** Re-keys the raw (field-id-keyed) form values by field `name`, matching the legacy submission format. */
  private mapValuesByFieldName(
    definition: DynamicFormDefinition,
    rawValues: Record<string, unknown>
  ): Record<string, unknown> {
    const named: Record<string, unknown> = {};
    for (const field of definition.fields) {
      named[field.name] = rawValues[field.id];
    }
    return named;
  }

  /** Builds the label/value pairs for output ("calculate"-driven) fields, for display after submit. */
  private buildResults(
    definition: DynamicFormDefinition,
    values: Record<string, unknown>
  ): { label: string; value: unknown }[] {
    const outputIds = this.outputFieldIds();
    return definition.fields
      .filter((field) => outputIds.has(field.id))
      .map((field) => ({ label: field.label, value: values[field.id] }));
  }

  protected getControl(fieldId: string): FormControl {
    return (this.form().get(fieldId) as FormControl) ?? new FormControl('');
  }

  protected getFieldById(fieldId?: string): DynamicField | undefined {
    if (!fieldId) return undefined;
    return this.definition()?.fields.find((f) => f.id === fieldId);
  }

  protected getSections() {
    return this.layout().sections ?? [];
  }

  protected getTableRows(table: DynamicTable): number[] {
    return Array.from({ length: table.rows }, (_, i) => i);
  }

  protected getTableCols(table: DynamicTable): number[] {
    return Array.from({ length: table.columns }, (_, i) => i);
  }

  protected getCell(table: DynamicTable, row: number, col: number) {
    return table.cells.find((c) => c.row === row && c.col === col);
  }

  /** Returns the cached calculation result for a table cell */
  protected resolveCalculation(tableId: string, row: number, col: number, expression?: string): string {
    const key = `${tableId}:${row}:${col}`;
    const cached = this.calcResults()[key];
    if (cached !== undefined) return cached;
    if (!expression) return '';
    return this.evalExpression(expression, tableId);
  }

  protected isFieldHidden(fieldId: string): boolean {
    return this.fieldStates()[fieldId]?.hidden ?? false;
  }

  /** Returns dynamically loaded options (from a "loadOptions" rule) when present, else the field's static data */
  protected getFieldOptions(field: DynamicField): DynamicDropDownOption[] {
    return this.fieldDynamicOptions()[field.id] ?? field.data ?? [];
  }

  protected isFieldReadonly(fieldId: string): boolean {
    if (this.readOnly()) return true;
    return this.fieldStates()[fieldId]?.readonly ?? false;
  }

  protected isFieldRequired(fieldId: string): boolean {
    return this.fieldStates()[fieldId]?.required ?? false;
  }

  protected getFieldErrors(field: DynamicField): string[] {
    const control = this.getControl(field.id);
    if (!control.touched || !control.errors) return [];

    const errors: string[] = [];
    if (control.errors['required']) errors.push(`${field.label} is required.`);
    if (control.errors['minlength']) errors.push(`${field.label} minimum length is ${field.minLength}.`);
    if (control.errors['maxlength']) errors.push(`${field.label} maximum length is ${field.maxLength}.`);
    if (control.errors['min']) errors.push(`${field.label} cannot be less than ${field.minValue}.`);
    if (control.errors['max']) errors.push(`${field.label} cannot exceed ${field.maxValue}.`);
    return errors;
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────

  private rebuildForm(): void {
    const definition = this.definition();

    if (!definition) {
      this.form.set(new FormGroup({}));
      this.calcResults.set({});
      this.fieldStates.set({});
      return;
    }

    const controls: Record<string, FormControl> = {};
    const initialStates: Record<string, FieldState> = {};

    for (const field of definition.fields) {
      const validators = [];
      if (field.isRequired) validators.push(Validators.required);
      if (field.minLength != null) validators.push(Validators.minLength(field.minLength));
      if (field.maxLength != null) validators.push(Validators.maxLength(field.maxLength));
      if (field.minValue != null) validators.push(Validators.min(field.minValue));
      if (field.maxValue != null) validators.push(Validators.max(field.maxValue));

      controls[field.id] = new FormControl(field.defaultValue ?? '', validators);
      initialStates[field.id] = {
        hidden: field.isHidden ?? false,
        readonly: field.isReadonly ?? false,
        required: field.isRequired ?? false,
      };
    }

    const formGroup = new FormGroup(controls);

    // Wire cascading dropdowns
    for (const field of definition.fields) {
      if (field.type !== 'cascadingDropdown' || !field.parentFieldId || !field.cascadingData) continue;

      const parent = formGroup.get(field.parentFieldId) as FormControl | null;
      const child = formGroup.get(field.id) as FormControl | null;
      if (!parent || !child) continue;

      parent.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        child.setValue('');
        child.markAsUntouched();
      });
    }

    // React to any value change: re-run rules + recalculate
    formGroup.valueChanges
      .pipe(debounceTime(80), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.runRules(definition, formGroup);
        this.runCalculations(definition, formGroup);
      });

    this.form.set(formGroup);
    this.fieldStates.set(initialStates);
    this.fieldDynamicOptions.set({});
    this.calcResults.set({});
    this.selectedFileName.set('');
    this.importResult.set(null);
    this.submitMessage.set('');

    // Initial pass
    this.runRules(definition, formGroup);
    this.runCalculations(definition, formGroup);
  }

  private runRules(definition: DynamicFormDefinition, formGroup: FormGroup): void {
    const effects = this.ruleEngine.evaluateAll(definition, formGroup);
    if (!effects.length) return;

    const states = { ...this.fieldStates() };
    const dynamicOptions = { ...this.fieldDynamicOptions() };
    for (const effect of effects) {
      this.applyEffect(effect, states, formGroup, definition, dynamicOptions);
    }
    this.fieldStates.set(states);
    this.fieldDynamicOptions.set(dynamicOptions);
  }

  private applyEffect(
    effect: RuleEffect,
    states: Record<string, FieldState>,
    formGroup: FormGroup,
    definition: DynamicFormDefinition,
    dynamicOptions: Record<string, DynamicDropDownOption[]>
  ): void {
    const field = definition.fields.find((f) => f.id === effect.fieldId);
    if (!field) return;

    if (!states[field.id]) {
      states[field.id] = { hidden: false, readonly: false, required: false };
    }

    if (effect.hidden !== undefined) states[field.id].hidden = effect.hidden;
    if (effect.readonly !== undefined) states[field.id].readonly = effect.readonly;

    if (effect.dataOptions !== undefined) {
      dynamicOptions[field.id] = effect.dataOptions;

      // Reset the current value if it's no longer a valid option (mirrors server dropdown reset)
      const control = formGroup.get(field.id);
      if (control && control.value && !effect.dataOptions.some((o) => o.value === control.value)) {
        control.setValue('', { emitEvent: false });
      }
    }

    if (effect.required !== undefined) {
      states[field.id].required = effect.required;
      const control = formGroup.get(field.id);
      if (control) {
        effect.required
          ? control.addValidators(Validators.required)
          : control.removeValidators(Validators.required);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }

    if (effect.value !== undefined) {
      const control = formGroup.get(field.id);
      if (control && control.value !== effect.value) {
        control.setValue(effect.value, { emitEvent: false });
      }
    }
  }

  private runCalculations(definition: DynamicFormDefinition, formGroup: FormGroup): void {
    const tables = definition.layout?.tables ?? [];
    if (!tables.length) return;

    const formValues = formGroup.getRawValue() as Record<string, unknown>;
    // Also key by field name so expressions like "density * 2" resolve
    for (const field of definition.fields) {
      formValues[field.name] = formValues[field.id];
    }

    const results: Record<string, string> = {};

    for (const table of tables) {
      for (const cell of table.cells) {
        if (cell.cellType === 'calculation' && cell.expression) {
          const key = `${table.id}:${cell.row}:${cell.col}`;
          const raw = this.calcEngine.evaluateCellExpression(
            cell.expression,
            tables,
            table.id,
            formValues,
            definition.fields
          );
          results[key] = raw !== null && raw !== undefined ? String(raw) : '';
        }
      }
    }

    this.calcResults.set(results);
  }

  private evalExpression(expression: string, tableId?: string): string {
    const definition = this.definition();
    if (!definition) return '';

    const formValues = this.form().getRawValue() as Record<string, unknown>;
    for (const field of definition.fields) {
      formValues[field.name] = formValues[field.id];
    }

    const tables = definition.layout?.tables ?? [];
    const raw = tableId
      ? this.calcEngine.evaluateCellExpression(expression, tables, tableId, formValues, definition.fields)
      : this.calcEngine.evaluate(expression, formValues, definition.fields);
    return raw !== null && raw !== undefined ? String(raw) : '';
  }

  private applyImportedData(values: Record<string, unknown>): void {
    const currentForm = this.form();
    for (const [fieldId, value] of Object.entries(values)) {
      const control = currentForm.get(fieldId);
      if (control) control.setValue(value);
    }
  }
}
